import React, { useState, FC, PropsWithChildren, ReactNode, useEffect, useCallback, Fragment } from 'react';
import makeStyles from '@mui/styles/makeStyles';
import { FormInputSelect } from 'components/form';
import { Box, Tabs, Tab, TabsProps, BoxProps } from '@mui/material';
import { useLocation, useHistory } from 'react-router-dom';
import { ContentViewType, SelectOption, TabsOption } from 'global';

interface Props<TabsValue extends string> {
  tabProps?: TabsProps;
  initialTab?: TabsValue;
  forceTabValue?: TabsValue;
  tabs: TabsOption<TabsValue>[];
  isSyncWithAnchor?: boolean;
  contentViewType?: ContentViewType;
  TabsLabelWrapper?: FC<PropsWithChildren>;
  tabsWrapperProps?: BoxProps;
  onTabChange?: (nextTabValue: TabsValue) => void;
}

export const TabsContainer = <TabsValue extends string = string>({
  tabs,
  tabProps,
  initialTab,
  isSyncWithAnchor = false,
  onTabChange,
  forceTabValue,
  TabsLabelWrapper,
  tabsWrapperProps = {},
  contentViewType = ContentViewType.Desktop,
}: Props<TabsValue>) => {
  const classes = useStyles();

  const location = useLocation();
  const { replace } = useHistory();

  const [currentTab, setCurrentTab] = useState<TabsValue>(initialTab ?? tabs?.[0]?.value);

  const setAnchor = useCallback(
    (value: TabsValue) => replace({ pathname: location.pathname, search: location.search, hash: `#${value}` }),
    [location.pathname, location.search, replace]
  );

  useEffect(() => {
    if (isSyncWithAnchor) {
      if (!location.hash) {
        setAnchor(currentTab);
      }
      const tab = tabs.find(t => t.urlAnchor === location.hash);
      if (tab) setCurrentTab(tab.value);
    }
  }, [currentTab, isSyncWithAnchor, location, setAnchor, tabs]);

  const handleChangeTab = useCallback(
    (nextTabValue: TabsValue) => {
      if (isSyncWithAnchor) {
        setAnchor(nextTabValue);
      }

      setCurrentTab(nextTabValue);

      if (onTabChange) onTabChange(nextTabValue);
    },
    [isSyncWithAnchor, onTabChange, setAnchor]
  );

  const tabsOptions = tabs
    .filter(({ isVisible = true }) => isVisible)
    .map(({ label, value }) => ({ label: label.toUpperCase(), value })) as SelectOption<TabsValue>[];

  useEffect(() => {
    if (forceTabValue) handleChangeTab(forceTabValue);
  }, [forceTabValue, handleChangeTab]);

  const tabsComponentByViewType: Record<ContentViewType, ReactNode> = {
    [ContentViewType.Desktop]: (
      <Tabs
        value={currentTab}
        variant="scrollable"
        sx={{ minHeight: 48 }}
        indicatorColor="primary"
        onChange={(_, nextTabValue) => handleChangeTab(nextTabValue)}
        {...tabProps}
      >
        {tabs
          .filter(({ isVisible = true }) => isVisible)
          .map(({ label, value, disabled = false, iconStart = undefined, iconEnd = undefined }) => {
            const icon = iconStart || iconEnd;
            const iconPosition = iconStart || iconEnd ? (iconStart ? 'start' : 'end') : undefined;
            return (
              <Tab
                key={label}
                icon={icon}
                value={value}
                label={label}
                disabled={disabled}
                iconPosition={iconPosition}
                sx={{ minHeight: 48, py: 1 }}
              />
            );
          })}
      </Tabs>
    ),
    [ContentViewType.Mobile]: (
      <FormInputSelect<TabsValue> value={currentTab} options={tabsOptions} onChange={handleChangeTab} />
    ),
  };

  return (
    <>
      <Box className={classes.tabsWrapper} {...tabsWrapperProps}>
        {TabsLabelWrapper ? (
          <TabsLabelWrapper>{tabsComponentByViewType[contentViewType]}</TabsLabelWrapper>
        ) : (
          tabsComponentByViewType[contentViewType]
        )}
      </Box>

      {tabs
        .filter(({ isVisible = true }) => isVisible)
        .map(({ value, component }) => (
          <Fragment key={value}>{value === currentTab && component}</Fragment>
        ))}
    </>
  );
};

const useStyles = makeStyles({
  tabsWrapper: {
    borderBottom: '1px solid lightgrey',
  },
});
